import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import * as api from "./api";
import type { AnalyticsFilters } from "./types";

const qk = {
  overview: (f: AnalyticsFilters) => ["zt-analytics", "overview", f] as const,
  cancelled: (f: AnalyticsFilters) => ["zt-analytics", "cancelled", f] as const,
  monthly: ["zt-analytics", "monthly"] as const,
  groupTenders: (dimension: string | null, category: string | null, f: AnalyticsFilters) =>
    ["zt-analytics", "group-tenders", dimension, category, f] as const,
  pricingItems: (f: AnalyticsFilters) => ["zt-analytics", "pricing-items", f] as const,
  pricingItemWinrate: (item: string | null, f: AnalyticsFilters) =>
    ["zt-analytics", "pricing-item-winrate", item, f] as const,
};

export const useOverview = (f: AnalyticsFilters) =>
  useQuery({ queryKey: qk.overview(f), queryFn: () => api.getOverview(f) });

export const useCancelled = (f: AnalyticsFilters) =>
  useQuery({ queryKey: qk.cancelled(f), queryFn: () => api.getCancelled(f) });

export const useGroupTenders = (
  dimension: string | null,
  category: string | null,
  f: AnalyticsFilters,
) =>
  useQuery({
    queryKey: qk.groupTenders(dimension, category, f),
    queryFn: () => api.getGroupTenders(dimension as string, category as string, f),
    enabled: !!dimension && category != null,
  });

export const usePricingItems = (f: AnalyticsFilters) =>
  useQuery({ queryKey: qk.pricingItems(f), queryFn: () => api.getPricingItems(f) });

export const usePricingItemWinrate = (item: string | null, f: AnalyticsFilters) =>
  useQuery({
    queryKey: qk.pricingItemWinrate(item, f),
    queryFn: () => api.getPricingItemWinrate(item as string, f),
    enabled: !!item,
  });

export const useMonthly = (poll = false) =>
  useQuery({
    queryKey: qk.monthly,
    queryFn: () => api.getMonthly(),
    refetchInterval: poll ? 5000 : false,
  });

export const useGenerateMonthly = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.generateMonthly(),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.monthly }),
  });
};

export const useAsk = () =>
  useMutation({
    mutationFn: (v: { question: string; filters?: AnalyticsFilters }) =>
      api.askAnalytics(v.question, v.filters),
  });
